import {RemoteConstantsModel} from "../models/remoteConstants.model";
import {FieldChange} from "./recordDiff";
import {SectionKey} from "./branchDiff";

// Atomic write operations the Resolve command will apply against Current's
// state. A single FieldChange usually maps to one ResolveAction, but a
// set-diff with both added and removed entries maps to several.
export interface ResolveAction {
    sectionKey: SectionKey;
    identity: string;
    path: string[];
    kind: 'scalar' | 'set-add' | 'set-remove';
    value: unknown;
}

export interface ResolveClassification {
    resolvable: boolean;
    actions: ResolveAction[];
    // When `resolvable` is false, an explanation suitable for a tooltip on
    // a disabled Resolve button.
    reason?: string;
}

// Returns the set of uids currently known to Current's state. Used by the
// reference-integrity check inside `classifyResolve` — if Resolve would
// write a uid into Current that doesn't exist anywhere in Current's data,
// we refuse with a tooltip pointing at the missing record.
export function collectCurrentKnownIds(state: RemoteConstantsModel): Set<string> {
    const ids = new Set<string>();
    state.units?.forEach(u => ids.add(u.id));
    state.buildings?.forEach(b => ids.add(b.id));
    state.tiles?.forEach(t => ids.add(t.id));
    state.resources?.forEach(r => ids.add(r.id));
    state.unitAttributes?.forEach(m => ids.add(m.uid));
    state.buildingAttributes?.forEach(m => ids.add(m.uid));
    state.techActionAttributes?.forEach(m => ids.add(m.uid));
    state.techPassiveAttributes?.forEach(m => ids.add(m.uid));
    state.rewards?.forEach(m => ids.add(m.uid));
    state.techTree?.forEach(n => ids.add(n.id));
    return ids;
}

// Decide whether a given FieldChange can be safely pulled from Other into
// Current, what writes to perform, and (if not) why.
//
// Classification rules:
//   - opaque             → refuse (out of scope for the simple path)
//   - scalar with target undefined (== "field disappears") → write undefined
//   - scalar with a uid that doesn't exist in Current → refuse, point at it
//   - scalar otherwise   → one ResolveAction (kind='scalar')
//   - set add (target-only entries) → ResolveAction(kind='set-add') per entry
//   - set remove (current-only entries) → ResolveAction(kind='set-remove') per entry
//   - set with both → both, applied in remove-then-add order at command time
//   - any uid entry whose target doesn't exist in Current → refuse the row
export function classifyResolve(
    fc: FieldChange,
    sectionKey: SectionKey,
    identity: string,
    currentKnownIds: Set<string>,
    knownIdsBothBranches: Set<string>,
): ResolveClassification {
    if (fc.kind === 'opaque') {
        return {
            resolvable: false,
            actions: [],
            reason: "This field is too complex to auto-resolve — replace the whole value manually.",
        };
    }

    const isUidStr = (v: unknown): v is string =>
        typeof v === 'string' && knownIdsBothBranches.has(v);

    if (fc.kind === 'scalar') {
        const target = fc.target;
        if (isUidStr(target) && !currentKnownIds.has(target)) {
            return {
                resolvable: false,
                actions: [],
                reason: `Other branch's value references a record (${shortUid(target)}) that doesn't exist on Current. Resolve that record's row first, then try again.`,
            };
        }
        return {
            resolvable: true,
            actions: [{
                sectionKey,
                identity,
                path: fc.path,
                kind: 'scalar',
                value: target,
            }],
        };
    }

    // set kind
    const added = fc.added ?? [];
    const removed = fc.removed ?? [];

    for (const entry of added) {
        if (isUidStr(entry) && !currentKnownIds.has(entry)) {
            return {
                resolvable: false,
                actions: [],
                reason: `The added entry (${shortUid(entry)}) references a record that doesn't exist on Current. Resolve that record first.`,
            };
        }
    }

    const actions: ResolveAction[] = [];
    // Apply removes first so an add of a renamed-to-same-display-key entry
    // works as expected (rare, but symmetric).
    for (const v of removed) {
        actions.push({sectionKey, identity, path: fc.path, kind: 'set-remove', value: v});
    }
    for (const v of added) {
        actions.push({sectionKey, identity, path: fc.path, kind: 'set-add', value: v});
    }

    return {resolvable: actions.length > 0, actions};
}

function shortUid(s: string): string {
    return s.length > 8 ? `${s.substring(0, 8)}…` : s;
}
